import { jsx, Fragment as Fragment$1, jsxs } from 'react/jsx-runtime';
import { IconMobileSheet24TextEditorCodeblock, IconTextEditor16Codeblock, IconMobileSheet24TextEditorDivider, IconTextEditor16Divider, IconMobileSheet24TextEditorTable, IconTextEditor16Table, IconMobileSheet24TextEditorQuote, IconTextEditor16Quote, IconMobileSheet24TextEditorCallout, IconTextEditor16Callout, IconMobileSheet24TextEditorToggleList, IconTextEditor16ToggleList, IconMobileSheet24TextEditorCheckList, IconTextEditor16CheckList, IconMobileSheet24TextEditorNumberList, IconTextEditor16NumberedList, IconMobileSheet24TextEditorBulletList, IconTextEditor16BulletList, IconMobileSheet24TextEditorHeading1, IconTextEditor16Heading1, IconMobileSheet24TextEditorHeading2, IconTextEditor16Heading2, IconMobileSheet24TextEditorHeading3, IconTextEditor16Heading3, IconMobileSheet24KindHighlight, IconDesktop16KindHighlight, IconMobileSheet24KindImage, IconDesktop16KindImage, IconMobileSheet24KindFile, IconDesktop16KindFile, IconMobileSheet24TextEditorLink, IconTextEditor16Link, IconMobileSheet24TextEditorLinkToPage, IconTextEditor16LinkToPage } from '@fabric-app/icons/svg';
import { forwardRef, useMemo, useEffect, useCallback, useImperativeHandle } from 'react';
import { fuzzySearchGroups } from './ContextualMenuComponent.js';
import { ContextualMenuSpecs } from '../ContextualMenuConfig.js';
import { useQuery } from '@tanstack/react-query';
import { Fragment } from '@tiptap/pm/model';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { getFromPath, getPath } from '../../utils.js';
import { Spinner } from '@fabric-app/ui/components/Spinner';
import { ContextualMenuPluginKey } from '../ContextualMenuPlugin.js';
import { ContextualMenuMeta } from '../ContextualMenuMeta.js';
import { TextSelection } from '@tiptap/pm/state';
import { prompt } from '../../../../store/alerts.js';
import { EditorSelector } from '../../../Components/EditorSelector.js';
import * as yup from 'yup';
import { formatUrlWithProtocol } from '@fabric-app/utils/url';
import { URLRegex } from '@fabric-app/utils/text';

const nodeTypeSpec = {
  paragraph: [
    {
      label: "Text",
      group: "basic",
      value: "paragraph",
      icon: /* @__PURE__ */ jsx(IconDesktop16KindHighlight, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24KindHighlight, {})
    },
    {
      label: "Image",
      group: "media",
      value: "emptyImage",
      icon: /* @__PURE__ */ jsx(IconDesktop16KindImage, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24KindImage, {})
    },
    {
      label: "File",
      group: "media",
      value: "emptyFile",
      icon: /* @__PURE__ */ jsx(IconDesktop16KindFile, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24KindFile, {})
    },
    {
      label: "Link",
      group: "media",
      value: "link",
      icon: /* @__PURE__ */ jsx(IconTextEditor16Link, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorLink, {})
    }
  ],
  heading: [
    {
      label: "Heading 1",
      group: "basic",
      value: "heading1",
      icon: /* @__PURE__ */ jsx(IconTextEditor16Heading1, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorHeading1, {})
    },
    {
      label: "Heading 2",
      group: "basic",
      value: "heading2",
      icon: /* @__PURE__ */ jsx(IconTextEditor16Heading2, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorHeading2, {})
    },
    {
      label: "Heading 3",
      group: "basic",
      value: "heading3",
      icon: /* @__PURE__ */ jsx(IconTextEditor16Heading3, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorHeading3, {})
    }
  ],
  fbcBulletList: {
    label: "Bullet list",
    group: "basic",
    value: "fbcBulletList",
    icon: /* @__PURE__ */ jsx(IconTextEditor16BulletList, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorBulletList, {})
  },
  fbcNumberList: {
    label: "Numbered list",
    group: "basic",
    value: "fbcNumberList",
    icon: /* @__PURE__ */ jsx(IconTextEditor16NumberedList, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorNumberList, {})
  },
  fbcCheckList: {
    label: "Checklist",
    group: "basic",
    value: "fbcCheckList",
    icon: /* @__PURE__ */ jsx(IconTextEditor16CheckList, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorCheckList, {})
  },
  fbcToggleList: {
    label: "Toggle list",
    group: "basic",
    value: "fbcToggleList",
    icon: /* @__PURE__ */ jsx(IconTextEditor16ToggleList, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorToggleList, {})
  },
  callout: {
    label: "Callout",
    group: "basic",
    value: "callout",
    icon: /* @__PURE__ */ jsx(IconTextEditor16Callout, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorCallout, {})
  },
  blockquote: {
    label: "Quote",
    group: "basic",
    value: "blockquote",
    icon: /* @__PURE__ */ jsx(IconTextEditor16Quote, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorQuote, {})
  },
  table: {
    label: "Table",
    group: "basic",
    value: "table",
    icon: /* @__PURE__ */ jsx(IconTextEditor16Table, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorTable, {})
  },
  horizontalRule: {
    label: "Divider",
    group: "basic",
    value: "horizontalRule",
    icon: /* @__PURE__ */ jsx(IconTextEditor16Divider, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorDivider, {})
  },
  codeBlock: {
    label: "Code block",
    group: "media",
    value: "codeBlock",
    icon: /* @__PURE__ */ jsx(IconTextEditor16Codeblock, {}),
    mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorCodeblock, {})
  }
};
const inlineGroup = {
  key: "inline",
  label: "Inline",
  items: [
    // {
    //   icon: <IconTextEditor16Mention />,
    //   label: 'Mention someone',
    //   value: 'mention',
    //   isAction: true,
    // },
    // {
    //   icon: <IconDesktop16KindTag />,
    //   label: 'Tag',
    //   value: 'tag',
    //   isAction: true,
    // },
    {
      icon: /* @__PURE__ */ jsx(IconTextEditor16LinkToPage, {}),
      mobileIcon: /* @__PURE__ */ jsx(IconMobileSheet24TextEditorLinkToPage, {}),
      label: "Link to item",
      value: "linkToItem",
      isAction: true
    }
  ]
};
const ContextualAddNodeMenu = /* @__PURE__ */ forwardRef(({ state: { query, view, spec, selection }, onCandidatesChange, editor }, ref) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      "contextual-add-node-menu",
      query
    ],
    queryFn: () => {
      const path = getFromPath(view.state.selection);
      if (path.length < 2) return [];
      let target = path[0];
      let targetParent = path[1];
      const decoSize = query.length + (spec.char?.length ?? 0);
      let replacement = false;
      if (targetParent.node.type.isInGroup("list") && target.index === 0) {
        target = targetParent;
        targetParent = path[2];
        replacement = false;
      } else if (target.node.inlineContent && target.node.textContent.length <= decoSize) {
        replacement = true;
      } else {
        replacement = false;
      }
      let contentMatch;
      if (replacement) {
        contentMatch = targetParent.node.contentMatchAt(target.index);
      } else {
        contentMatch = targetParent.node.contentMatchAt(target.index + 1);
      }
      if (!contentMatch) {
        return [];
      }
      const basicGroup = {
        key: "basic",
        label: "Basic blocks",
        items: []
      };
      const mediaGroup = {
        key: "media",
        label: "Media",
        items: []
      };
      for (let i = 0; i < contentMatch.edgeCount; i++) {
        const edge = contentMatch.edge(i);
        const nodeSpec = nodeTypeSpec[edge.type.name];
        if (!nodeSpec) continue;
        const specs = Array.isArray(nodeSpec) ? nodeSpec : [
          nodeSpec
        ];
        for (const spec2 of specs) {
          if (spec2.group === "basic") {
            basicGroup.items.push(spec2);
          } else if (spec2.group === "media") {
            mediaGroup.items.push(spec2);
          }
        }
      }
      return fuzzySearchGroups([
        basicGroup,
        {
          ...inlineGroup
        },
        {
          ...mediaGroup
        }
      ], query);
    }
  });
  const candidates = useMemo(() => {
    return data?.flatMap((group) => group.items.map((item) => item.value)) ?? [];
  }, [
    data
  ]);
  useEffect(() => {
    onCandidatesChange(candidates);
  }, [
    candidates,
    onCandidatesChange
  ]);
  const onSelect = useCallback(async (value) => {
    const pluginState = ContextualMenuPluginKey.getState(view.state);
    if (!pluginState || pluginState.query === null || pluginState.active === null) return;
    ContextualMenuMeta.reset(view);
    view.focus();
    const resolved = view.state.selection.$from;
    const path = getPath(resolved);
    if (path.length < 2) return [];
    let target = path[0];
    let targetParent = path[1];
    let replacement = false;
    if (targetParent.node.type.isInGroup("list") && target.index === 0) {
      target = targetParent;
      targetParent = path[2];
      replacement = false;
    } else if (target.node.inlineContent && target.node.textContent.length < 1) {
      replacement = true;
    } else {
      replacement = false;
    }
    let position = replacement ? target.start : target.end + 2;
    if (value === "linkToItem") {
      const tr2 = view.state.tr;
      const char = ContextualMenuSpecs.linkToItem.char ?? "";
      if (!pluginState.mobile) tr2.replaceWith(pluginState.range.from, pluginState.range.to, Fragment.from(view.state.schema.text(char))).setSelection(TextSelection.create(tr2.doc, pluginState.range.from + char.length));
      tr2.setMeta(ContextualMenuPluginKey, new ContextualMenuMeta(ContextualMenuSpecs.linkToItem, pluginState.range.from, pluginState.range.from + (ContextualMenuSpecs.linkToItem.char?.length ?? 0), true, pluginState.mobile));
      view.dispatch(tr2);
      return;
    }
    if (value === "link") {
      const result = await prompt({
        confirmLabel: "Insert",
        cancelLabel: "Cancel",
        labels: {
          text: "Text",
          link: "Link"
        },
        defaultValue: {
          text: "",
          link: ""
        },
        placeholders: {
          link: "Add a link"
        },
        autoFocus: "link",
        validation: yup.object({
          text: yup.string().optional(),
          link: yup.string().required().matches(URLRegex, "Invalid URL")
        })
      });
      if (!result) return;
      const { text, link } = result;
      const tr2 = view.state.tr;
      tr2.replaceWith(pluginState.range.from, pluginState.range.to, Fragment.from(view.state.schema.text(text || link, [
        view.state.schema.marks.link.create({
          href: formatUrlWithProtocol(link)
        })
      ])));
      view.dispatch(tr2);
      return;
    }
    const decoSize = pluginState.mobile ? 0 : pluginState.query.length + (spec.char?.length ?? 0);
    if (target.node.inlineContent && target.node.textContent.length <= decoSize) {
      replacement = true;
    }
    position = replacement ? target.start : target.end + 2;
    const deleteRange = pluginState.summoned && pluginState.mobile ? {
      from: 0,
      to: 0
    } : {
      from: pluginState.range.from,
      to: pluginState.range.to
    };
    if (value === "table") {
      editor.chain().deleteRange(deleteRange).setTextSelection(position).insertTable().run();
    }
    const tr = view.state.tr;
    let newContent = null;
    const schema = view.state.schema;
    let isAtom = false;
    switch (value) {
      case "heading1":
        newContent = schema.nodes.heading.createAndFill({
          level: 1
        });
        break;
      case "heading2":
        newContent = schema.nodes.heading.createAndFill({
          level: 2
        });
        break;
      case "heading3":
        newContent = schema.nodes.heading.createAndFill({
          level: 3
        });
        break;
      default:
        newContent = schema.nodes[value].createAndFill();
        isAtom = schema.nodes[value].isAtom;
        break;
    }
    if (!newContent) return;
    let selectionPos;
    if (replacement) {
      tr.replaceWith(target.pos, target.end, newContent);
      selectionPos = tr.mapping.map(target.start, -1);
    } else {
      tr.insert(target.end, newContent).delete(deleteRange.from, deleteRange.to);
      selectionPos = tr.mapping.map(target.end + 1, -1);
    }
    if (isAtom) {
      tr.insert(selectionPos + newContent.nodeSize, schema.nodes.paragraph.create()).setSelection(TextSelection.create(tr.doc, selectionPos + newContent.nodeSize));
    } else {
      tr.setSelection(TextSelection.create(tr.doc, selectionPos));
    }
    view.dispatch(tr);
  }, [
    view,
    spec.char?.length,
    editor
  ]);
  useImperativeHandle(ref, () => ({
    onSelect
  }));
  return /* @__PURE__ */ jsx(Fragment$1, {
    children: isLoading || !data ? /* @__PURE__ */ jsx(EditorSelector.Group, {
      children: /* @__PURE__ */ jsxs(EditorSelector.GroupTitle, {
        children: [
          /* @__PURE__ */ jsx(Spinner, {
            size: 16,
            color: cssVar["color-text-tertiary"]
          }),
          "Loading..."
        ]
      })
    }) : data.length > 0 ? data.map((group) => {
      return /* @__PURE__ */ jsxs(EditorSelector.Group, {
        children: [
          group.label && /* @__PURE__ */ jsxs(EditorSelector.GroupTitle, {
            children: [
              group.label,
              ":"
            ]
          }),
          group.items.map((item) => {
            return /* @__PURE__ */ jsx(EditorSelector.Item, {
              "data-keyboard-selected": item.value === selection,
              onClick: () => onSelect(item.value),
              value: item.value,
              label: item.label,
              icon: item.icon,
              mobileIcon: item.mobileIcon,
              "data-prevent-default": true
            }, item.value);
          })
        ]
      }, group.key);
    }) : /* @__PURE__ */ jsx(EditorSelector.Group, {
      children: /* @__PURE__ */ jsx(EditorSelector.GroupTitle, {
        children: "No items found"
      })
    })
  });
});
ContextualAddNodeMenu.displayName = "ContextualAddNodeMenu";

export { ContextualAddNodeMenu as default, inlineGroup, nodeTypeSpec };
//# sourceMappingURL=ContextualAddNodeMenu.js.map
